<script setup lang="ts">
import { computed, ref } from 'vue';
import { onClickOutside } from '@/composables/onClickOutside';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { useThemeStore } from '@/stores/theme';

const auth = useAuthStore();
const theme = useThemeStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const showBack = computed(() => route.name !== 'home' && route.name !== 'login');
function back() {
  if (window.history.length > 1) router.back();
  else router.push({ name: 'home' });
}

const menuOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);
onClickOutside(menuRef, () => (menuOpen.value = false));

async function logout() {
  menuOpen.value = false;
  await auth.logout();
  router.push({ name: 'login' });
}

function clearCache() {
  menuOpen.value = false;
  auth.clearLocalCache();
  void auth.fetchMe().then((ok) => {
    if (!ok) router.push({ name: 'login' });
  });
}

const themeLabel = computed(() =>
  theme.mode === 'system' ? '跟隨系統' : theme.mode === 'light' ? '淺色' : '深色',
);
</script>

<template>
  <header
    class="sticky top-0 z-10 flex items-center gap-2 border-b border-gray-100 bg-white dark:bg-zinc-900/90 px-4 py-3 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/85"
  >
    <button
      v-if="showBack"
      class="rounded-full p-1 text-ink-muted hover:bg-gray-100 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-800"
      aria-label="back"
      @click="back"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="m15 18-6-6 6-6" />
      </svg>
    </button>

    <RouterLink to="/" class="flex items-center gap-2 font-bold text-line">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="10" />
      </svg>
      <span>{{ t('app.name') }}</span>
    </RouterLink>

    <button
      type="button"
      class="ml-1 inline-flex items-center justify-center rounded-full p-1.5 text-ink-muted dark:text-zinc-400 transition hover:bg-gray-100 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-800"
      :title="`主題：${themeLabel}（點擊切換）`"
      :aria-label="`Theme: ${themeLabel}`"
      @click="theme.cycle"
    >
      <!-- system: half-moon-in-circle -->
      <svg
        v-if="theme.mode === 'system'"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <rect x="3" y="4" width="18" height="14" rx="2" />
        <path d="M7 21h10" />
        <path d="M12 17v4" />
      </svg>
      <!-- light: sun -->
      <svg
        v-else-if="theme.mode === 'light'"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
      <!-- dark: moon -->
      <svg
        v-else
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
      </svg>
    </button>

    <div ref="menuRef" class="relative ml-auto">
      <button
        v-if="auth.user"
        class="flex items-center gap-1 rounded-full px-2 py-1 text-sm text-ink-muted dark:text-zinc-400 hover:bg-gray-100 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-800"
        @click="menuOpen = !menuOpen"
      >
        <span class="max-w-[8rem] truncate">{{ auth.user.displayName }}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      <div
        v-if="menuOpen"
        class="absolute right-0 mt-1 w-44 overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-200 dark:ring-zinc-700 dark:bg-zinc-900 dark:ring-zinc-800"
      >
        <button
          class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:bg-zinc-900 dark:hover:bg-zinc-800"
          @click="clearCache"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
          </svg>
          清除本機快取
        </button>
        <button
          class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-rose-500 hover:bg-rose-50 dark:bg-rose-900/30 dark:hover:bg-rose-900/30"
          @click="logout"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
          </svg>
          登出
        </button>
      </div>
    </div>
  </header>
</template>